import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Alert from 'react-bootstrap/Alert'
import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from '../../components/ConfirmationModal'
import DebugAccordion from '../../components/DebugAccordion'
import Icon from '../../components/Icon.jsx'
import Loadable from '../../components/Loadable'
import LoadingAnimation from '../../components/LoadingAnimation.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import ConfigTemplateNav, { ConfigTemplateSubNav } from './components/ConfigTemplateNav'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchConfigTemplate, updateConfigTemplate } from '../../state/actions/configTemplateActions'
import { fetchDeviceSchemas } from '../../state/actions/deviceSchemaActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'

import { configTemplateSignal, configTemplateLoadedSignal, configTemplateErrorSignal } from '../../state/signals/configTemplateSignals'
import { deviceSchemasSignal, deviceSchemasLoadedSignal, deviceSchemasErrorSignal } from '../../state/signals/deviceSchemaSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ConfigTemplateDeviceList() {

    const navigate = useNavigate()
    const { beId, configTemplateId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/templates`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, userPermissionsSummary } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewConfigTemplates) {
            addNotification('You are not authorized to view config templates for this account.', NotificationTypes.ERROR, true)
            navigate('/')
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => userPermissionsSummary?.value?.canEditConfigTemplates)

    useEffect(() => {
        if (beIdChanged) return
        fetchConfigTemplate(beId, configTemplateId, config, accessToken)
        fetchDeviceSchemas(config, accessToken)
        fetchSchemaManifest()
    }, [beId, beIdChanged, configTemplateId, config, accessToken])

    const configTemplateNameSignal = useSignal('')
    const configTemplateVersionSignal = useSignal('')

    useSignalEffect(() => {
        configTemplateNameSignal.value = configTemplateSignal.value?.name ?? ''
        configTemplateVersionSignal.value = configTemplateSignal.value?.utgSchemaVersion ?? ''
    })

    const schemaVersionOptionsComputed = useComputed(() => {
        const configs = schemaManifestSignal.value?.configs
        if (!configs?.length) return []
        return [...configs]
            .sort((a, b) => a.utgVersion < b.utgVersion ? 1 : -1)
            .map(({ utgVersion }) => (
                <option value={utgVersion} key={utgVersion}>{utgVersion}</option>
            ))
    })

    const handleNameSave = (event) => {
        event.preventDefault()
        updateConfigTemplate(beId, { ...configTemplateSignal.value, name: configTemplateNameSignal.value, utgSchemaVersion: configTemplateVersionSignal.value }, config, accessToken)
            .then(result => {
                configTemplateSignal.value = result
                addNotification('Config template updated.', NotificationTypes.SUCCESS, true)
            })
            .catch(error => addNotification(`Unable to update. ${error.toString()}`, NotificationTypes.ERROR, false))
    }

    const parsedConfigComputed = useComputed(() => {
        try { return JSON.parse(configTemplateSignal.value?.config || '{}') } catch { return {} }
    })

    const devicesComputed = useComputed(() => {
        return parsedConfigComputed.value?.deviceConfig?.device ?? []
    })

    const showDeleteModalSignal = useSignal(false)
    const confirmDeviceTypeSignal = useSignal('')
    const confirmTaskDescSignal = useSignal('')

    const accordionData = [
        { name: 'Devices', data: devicesComputed.value },
        { name: 'Config Template', data: configTemplateSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    function DeviceTableRows({ devices }) {
        if (!configTemplateLoadedSignal.value) {
            return <tr><td colSpan={4}><LoadingAnimation /></td></tr>
        }
        if (devices && devices.length == 0) {
            return (
                <tr><td colSpan="4" className="text-center">This config template does not contain any configured devices</td></tr>
            )
        }

        let rows = []
        devices.forEach(device => {
            rows.push(
                <DeviceRow device={device} key={`${device.deviceType}${device.taskDesc}`} />
            )
        })

        return rows
    }

    function DeviceRow({ device }) {
        let deviceType = device.deviceType
        let taskDesc = device.taskDesc
        let encodedDeviceType = encodeURIComponent(deviceType)
        return (
            <tr>
                <td><Link to={`./${encodedDeviceType}?desc=${taskDesc}`}>{device.deviceType}</Link></td>
                <td>{device.taskDesc}</td>
                <td>{device.terminalList}</td>
                <td>
                    {canEditComputed.value && <Link to={`./${encodedDeviceType}?desc=${taskDesc}`}>
                        <Button variant="outline-primary" size="sm"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button>
                    </Link>}{' '}
                    {canEditComputed.value && <Button variant="outline-danger" size="sm" onClick={() => showModal(deviceType, taskDesc)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>}
                </td>
            </tr>
        )
    }

    const deleteDevice = () => {
        showDeleteModalSignal.value = false

        const parsed = parsedConfigComputed.value
        const filteredDevices = (parsed?.deviceConfig?.device ?? []).filter(device =>
            !(device.deviceType == confirmDeviceTypeSignal.value && device.taskDesc == confirmTaskDescSignal.value))

        const updatedConfig = {
            ...parsed,
            deviceConfig: { ...parsed.deviceConfig, device: filteredDevices }
        }

        updateConfigTemplate(beId, { ...configTemplateSignal.value, config: JSON.stringify(updatedConfig) }, config, accessToken)
            .then(result => {
                configTemplateSignal.value = result
                addNotification('The Device was successfully removed.', NotificationTypes.SUCCESS, true)
            })
            .catch(error => addNotification(`Unable to remove the Device. ${error.toString()}`, NotificationTypes.ERROR, false))
    }

    const showModal = (deviceType, taskDesc) => {
        confirmDeviceTypeSignal.value = deviceType
        confirmTaskDescSignal.value = taskDesc
        showDeleteModalSignal.value = true
    }

    function NewDeviceDropdown() {
        const deviceSchemas = deviceSchemasSignal.value
        if (!deviceSchemas?.length) return null

        const deviceOptions = deviceSchemas.map(({ name, schemaValue }) => {
            const displayName = schemaValue?.properties?.config?.properties?.deviceType?.default || name
            const escapedName = encodeURIComponent(name)
            return (
                <Dropdown.Item as={Link} to={`./${escapedName}`} key={name}>{displayName}</Dropdown.Item>
            )
        })

        return (
            <DropdownButton id="dropdown-add-device"
                variant="outline-secondary"
                title="New Device"
                className="mx-3">
                {deviceOptions}
            </DropdownButton>
        )
    }

    return (
        <>
            {canEditComputed.value && (
                <ConfirmationModal
                    show={showDeleteModalSignal.value}
                    handleCancel={() => showDeleteModalSignal.value = false}
                    handleConfirm={deleteDevice}
                    message={`Are you sure you want to delete ${confirmTaskDescSignal.value}? This action cannot be undone.`}
                    title={`Deleting ${confirmTaskDescSignal.value}`} />
            )}
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/templates` }}>
                    Config Templates
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    {configTemplateLoadedSignal.value ? <>Manage {configTemplateSignal.value?.name}</> : <>Loading...</>}
                </Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title="Manage Config Template" canEditSignal={canEditComputed} />
            </Stack>
            <Form className="px-3 mb-2" onSubmit={handleNameSave}>
                <Form.Group as={Row} className="mb-2" controlId="ConfigTemplateNameDevices">
                    <Form.Label column sm="2" className="text-end">Name:</Form.Label>
                    <Col sm="10">
                        <Form.Control
                            type="text"
                            required
                            value={configTemplateNameSignal.value}
                            onInput={e => configTemplateNameSignal.value = e.target.value}
                            readOnly={!canEditComputed.value}
                        />
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-2" controlId="ConfigTemplateVersionDevices">
                    <Form.Label column sm="2" className="text-end">Schema Version:</Form.Label>
                    <Col sm="8">
                        <Form.Control as="select" value={configTemplateVersionSignal.value} onChange={e => configTemplateVersionSignal.value = e.target.value} disabled={!canEditComputed.value}>
                            <option value="">Select Schema Version</option>
                            {schemaVersionOptionsComputed.value}
                        </Form.Control>
                    </Col>
                    {canEditComputed.value && (
                        <Col sm="2">
                            <button type="submit" className="btn btn-primary btn-sm">Save</button>
                        </Col>
                    )}
                </Form.Group>
            </Form>
            <ConfigTemplateNav activeKey="2" />
            <ConfigTemplateSubNav activeKey="4" />
            <Loadable loadedSignals={[configTemplateLoadedSignal, deviceSchemasLoadedSignal]} errorSignals={[configTemplateErrorSignal, deviceSchemasErrorSignal]}>
                <Alert variant="info" className="mx-3 py-2 small">
                    Devices in a config template only update devices that already exist on the target UTG.
                    A device is matched on Device Type and API Terminal ID; if no match is found, it is skipped when the template is applied.
                </Alert>
                <Table responsive striped hover size="sm">
                    <thead>
                        <tr>
                            <th>Device Type</th>
                            <th>Device Name</th>
                            <th>API Terminal ID</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody><DeviceTableRows devices={devicesComputed.value} /></tbody>
                </Table>
                {canEditComputed.value && <NewDeviceDropdown />}
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}
